import React, { Component } from "react";
import { CheckCircle, AlertCircle, RefreshCw, Send, Hotel } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

class ConfirmPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      token: "",
      booking: null,
      loading: true,
      error: null,
      confNo: "",
      submitting: false,
      success: false,
      alreadyDone: false,
      showReject: false,
      rejectReason: "",
      rejecting: false,
      rejected: false,
    };
  }

  componentDidMount() {
    // Extract token from URL: /confirm/TOKEN
    const path = window.location.pathname;
    const parts = path.split("/");
    const token = parts[parts.length - 1];
    this.setState({ token }, () => this.loadBooking());
  }

  async loadBooking() {
    const { token } = this.state;
    if (!token || token === "confirm") {
      this.setState({ loading: false, error: "Invalid confirmation link." });
      return;
    }
    try {
      const res = await fetch(`${API}/confirm/${token}`);
      const data = await res.json();
      if (data.alreadyRejected) {
        this.setState({ loading: false, rejected: true, booking: data.data });
        return;
      }
      if (data.alreadyConfirmed) {
        this.setState({
          loading: false,
          alreadyDone: true,
          booking: data.data,
        });
        return;
      }
      if (!data.status) {
        this.setState({
          loading: false,
          error: data.message || "Invalid link.",
        });
        return;
      }
      this.setState({ loading: false, booking: data.data });
    } catch (e) {
      this.setState({
        loading: false,
        error: "Failed to load booking. Please check your internet connection.",
      });
    }
  }

  async handleSubmit() {
    const { token, confNo } = this.state;
    if (!confNo.trim()) {
      this.setState({ error: "Please enter the confirmation number." });
      return;
    }
    this.setState({ submitting: true, error: null });
    try {
      const res = await fetch(`${API}/confirm/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conf_no: confNo.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save.");
      this.setState({ success: true, submitting: false });
    } catch (e) {
      this.setState({ error: e.message, submitting: false });
    }
  }

  async handleReject() {
    const { token, rejectReason } = this.state;
    if (!rejectReason.trim()) {
      this.setState({ error: "Please enter a reason for rejection." });
      return;
    }
    this.setState({ rejecting: true, error: null });
    try {
      const res = await fetch(`${API}/confirm/${token}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: rejectReason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed");
      this.setState({ rejected: true, rejecting: false, showReject: false });
    } catch (e) {
      this.setState({ error: e.message, rejecting: false });
    }
  }

  render() {
    const {
      loading,
      booking,
      error,
      confNo,
      submitting,
      success,
      alreadyDone,
      rejected,
      showReject,
      rejectReason,
      rejecting,
    } = this.state;

    const fmt = (d) =>
      d
        ? new Date(d).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : "—";

    return (
      <div
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg,#EEF2FF 0%,#F0F9FF 50%,#FFFBEB 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          fontFamily: "Arial,sans-serif",
        }}
      >
        <div style={{ width: "100%", maxWidth: 540 }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div
              style={{
                width: 56,
                height: 56,
                background: "linear-gradient(135deg,#1E2D4F,#2C4070)",
                borderRadius: 16,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 14,
                boxShadow: "0 8px 24px rgba(30,45,79,0.25)",
              }}
            >
              <Hotel size={26} color="#C9A84C" />
            </div>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#1E2D4F",
                margin: "0 0 4px",
                letterSpacing: "-0.02em",
              }}
            >
              KTS Booking
            </h1>
            <p
              style={{
                fontSize: 13,
                color: "#9CA3AF",
                margin: 0,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Hotel Confirmation Portal
            </p>
          </div>

          {/* Card */}
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              boxShadow: "0 8px 40px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            {loading && (
              <div
                style={{
                  padding: "60px 32px",
                  textAlign: "center",
                  color: "#9CA3AF",
                }}
              >
                <RefreshCw
                  size={28}
                  style={{
                    animation: "spin 1s linear infinite",
                    marginBottom: 14,
                  }}
                />
                <p style={{ fontSize: 15 }}>Loading booking details…</p>
              </div>
            )}

            {!loading && alreadyDone && booking && (
              <div style={{ padding: "40px 32px", textAlign: "center" }}>
                <CheckCircle
                  size={48}
                  color="#059669"
                  style={{ marginBottom: 16 }}
                />
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#065F46",
                    margin: "0 0 8px",
                  }}
                >
                  Already Confirmed
                </h2>
                <p
                  style={{ fontSize: 14, color: "#6B7280", margin: "0 0 20px" }}
                >
                  This booking has already been confirmed.
                </p>
                <div
                  style={{
                    background: "#ECFDF5",
                    border: "1px solid #A7F3D0",
                    borderRadius: 12,
                    padding: 16,
                    display: "inline-block",
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      color: "#065F46",
                      margin: "0 0 4px",
                    }}
                  >
                    Confirmation Number
                  </p>
                  <p
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: "#065F46",
                      margin: 0,
                    }}
                  >
                    {booking.conf_no}
                  </p>
                </div>
              </div>
            )}

            {!loading && !success && rejected && (
              <div style={{ padding: "40px 32px" }}>
                {/* Red header banner */}
                <div
                  style={{
                    background: "#DC2626",
                    margin: "-0px",
                    padding: "20px 24px",
                    marginBottom: 24,
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: 32, marginBottom: 8 }}>✗</div>
                  <h2
                    style={{
                      color: "#fff",
                      fontSize: 20,
                      fontWeight: 800,
                      margin: 0,
                    }}
                  >
                    Booking Rejected
                  </h2>
                  <p
                    style={{
                      color: "#FECACA",
                      fontSize: 13,
                      margin: "6px 0 0",
                    }}
                  >
                    This reservation could not be accommodated
                  </p>
                </div>

                {booking && (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginBottom: 20,
                    }}
                  >
                    <tr style={{ background: "#FEF2F2" }}>
                      <td
                        style={{
                          padding: "10px 14px",
                          fontSize: 13,
                          color: "#6B7280",
                          border: "1px solid #FECACA",
                          width: "40%",
                        }}
                      >
                        Booking Ref
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#991B1B",
                          border: "1px solid #FECACA",
                        }}
                      >
                        #{booking.id}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          padding: "10px 14px",
                          fontSize: 13,
                          color: "#6B7280",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        Guest
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          fontSize: 13,
                          color: "#111827",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        {booking.guest_name}
                      </td>
                    </tr>
                    <tr style={{ background: "#F9FAFB" }}>
                      <td
                        style={{
                          padding: "10px 14px",
                          fontSize: 13,
                          color: "#6B7280",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        Hotel
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#111827",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        {booking.hotel_name}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          padding: "10px 14px",
                          fontSize: 13,
                          color: "#6B7280",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        Check In / Out
                      </td>
                      <td
                        style={{
                          padding: "10px 14px",
                          fontSize: 13,
                          color: "#111827",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        {String(booking.check_in || "").slice(0, 10)} →{" "}
                        {String(booking.check_out || "").slice(0, 10)}
                      </td>
                    </tr>
                    {booking.rejection_reason && (
                      <tr style={{ background: "#FEF2F2" }}>
                        <td
                          style={{
                            padding: "10px 14px",
                            fontSize: 13,
                            color: "#991B1B",
                            fontWeight: 700,
                            border: "1px solid #FECACA",
                          }}
                        >
                          Rejection Reason
                        </td>
                        <td
                          style={{
                            padding: "10px 14px",
                            fontSize: 13,
                            color: "#DC2626",
                            fontWeight: 600,
                            border: "1px solid #FECACA",
                          }}
                        >
                          {booking.rejection_reason}
                        </td>
                      </tr>
                    )}
                    {booking.rejected_at && (
                      <tr>
                        <td
                          style={{
                            padding: "10px 14px",
                            fontSize: 13,
                            color: "#6B7280",
                            border: "1px solid #E5E7EB",
                          }}
                        >
                          Rejected At
                        </td>
                        <td
                          style={{
                            padding: "10px 14px",
                            fontSize: 12,
                            color: "#9CA3AF",
                            border: "1px solid #E5E7EB",
                            fontFamily: "JetBrains Mono,monospace",
                          }}
                        >
                          {new Date(booking.rejected_at).toLocaleString(
                            "en-IN",
                          )}
                        </td>
                      </tr>
                    )}
                  </table>
                )}

                <div
                  style={{
                    background: "#FFF8E7",
                    border: "1px solid #FDE68A",
                    borderRadius: 10,
                    padding: 14,
                    textAlign: "center",
                  }}
                >
                  <p style={{ fontSize: 13, color: "#92400E", margin: 0 }}>
                    KTS team and the guest have been notified. Please close this
                    window.
                  </p>
                </div>
              </div>
            )}

            {!loading && error && !booking && (
              <div style={{ padding: "40px 32px", textAlign: "center" }}>
                <AlertCircle
                  size={44}
                  color="#DC2626"
                  style={{ marginBottom: 14 }}
                />
                <h2
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#991B1B",
                    margin: "0 0 8px",
                  }}
                >
                  Link Error
                </h2>
                <p style={{ fontSize: 14, color: "#6B7280" }}>{error}</p>
              </div>
            )}

            {!loading && success && (
              <div style={{ padding: "48px 32px", textAlign: "center" }}>
                <CheckCircle
                  size={56}
                  color="#059669"
                  style={{ marginBottom: 18 }}
                />
                <h2
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#065F46",
                    margin: "0 0 10px",
                  }}
                >
                  Confirmation Saved!
                </h2>
                <p
                  style={{
                    fontSize: 15,
                    color: "#374151",
                    lineHeight: 1.6,
                    margin: "0 0 20px",
                  }}
                >
                  The confirmation number has been recorded.
                  <br />
                  An email has been sent to the guest and KTS team.
                </p>
                <div
                  style={{
                    background: "#ECFDF5",
                    border: "1px solid #A7F3D0",
                    borderRadius: 12,
                    padding: 16,
                    display: "inline-block",
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      color: "#065F46",
                      margin: "0 0 4px",
                    }}
                  >
                    Saved Confirmation Number
                  </p>
                  <p
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: "#065F46",
                      margin: 0,
                    }}
                  >
                    {confNo}
                  </p>
                </div>
                <p style={{ fontSize: 13, color: "#9CA3AF", marginTop: 20 }}>
                  You may close this window.
                </p>
              </div>
            )}

            {!loading && !success && !alreadyDone && !rejected && booking && (
              <>
                {/* Booking summary */}
                <div style={{ background: "#1E2D4F", padding: "20px 28px" }}>
                  <p
                    style={{
                      fontSize: 11,
                      color: "#7FA8C9",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      margin: "0 0 4px",
                    }}
                  >
                    Reservation Request
                  </p>
                  <h2
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#fff",
                      margin: "0 0 4px",
                    }}
                  >
                    {booking.guest_name}
                  </h2>
                  <p style={{ fontSize: 14, color: "#B8C8E0", margin: 0 }}>
                    {booking.hotel_name}
                  </p>
                </div>

                <div style={{ padding: "0 28px" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      margin: "20px 0",
                    }}
                  >
                    {[
                      ["Check In", fmt(booking.check_in)],
                      ["Check Out", fmt(booking.check_out)],
                      ["Nights", booking.total_nights || booking.no_of_nights],
                      ["Room Type", booking.room_type || "As available"],
                      ["Single Rooms", booking.single_rooms],
                      ["Double Rooms", booking.double_rooms],
                      ...(booking.extra_bed > 0
                        ? [["Extra Beds", booking.extra_bed]]
                        : []),
                    ].map(([l, v], i) => (
                      <tr
                        key={l}
                        style={{ background: i % 2 === 0 ? "#F9FAFB" : "#fff" }}
                      >
                        <td
                          style={{
                            padding: "9px 14px",
                            fontSize: 13,
                            color: "#6B7280",
                            border: "1px solid #F3F4F6",
                            width: "45%",
                          }}
                        >
                          {l}
                        </td>
                        <td
                          style={{
                            padding: "9px 14px",
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#111827",
                            border: "1px solid #F3F4F6",
                          }}
                        >
                          {v || "—"}
                        </td>
                      </tr>
                    ))}
                  </table>
                </div>

                {/* Confirmation form */}
                <div style={{ padding: "0 28px 28px" }}>
                  <div
                    style={{
                      background: "#EFF6FF",
                      border: "1.5px solid #BFDBFE",
                      borderRadius: 12,
                      padding: "18px 20px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#1E2D4F",
                        margin: "0 0 14px",
                      }}
                    >
                      ✏️ Enter Hotel Confirmation Number
                    </p>
                    <input
                      type="text"
                      value={confNo}
                      onChange={(e) =>
                        this.setState({ confNo: e.target.value, error: null })
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" && this.handleSubmit()
                      }
                      placeholder="e.g. TAJ-2024-00123"
                      autoFocus
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        border: `2px solid ${error ? "#FCA5A5" : "#93C5FD"}`,
                        borderRadius: 10,
                        padding: "12px 16px",
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#1E2D4F",
                        outline: "none",
                        background: "#fff",
                        letterSpacing: "0.04em",
                        marginBottom: error ? 6 : 14,
                      }}
                    />
                    {error && (
                      <p
                        style={{
                          fontSize: 12,
                          color: "#DC2626",
                          margin: "0 0 12px",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <AlertCircle size={13} /> {error}
                      </p>
                    )}
                    <button
                      onClick={() => this.handleSubmit()}
                      disabled={submitting || !confNo.trim()}
                      style={{
                        width: "100%",
                        padding: "13px",
                        border: "none",
                        borderRadius: 10,
                        background:
                          submitting || !confNo.trim() ? "#9CA3AF" : "#1E2D4F",
                        color: "#fff",
                        fontSize: 14,
                        fontWeight: 700,
                        cursor:
                          submitting || !confNo.trim()
                            ? "not-allowed"
                            : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      {submitting ? (
                        <>
                          <RefreshCw
                            size={15}
                            style={{ animation: "spin 1s linear infinite" }}
                          />{" "}
                          Saving…
                        </>
                      ) : (
                        <>
                          <Send size={15} /> Save &amp; Confirm Booking
                        </>
                      )}
                    </button>
                    <p
                      style={{
                        fontSize: 11,
                        color: "#9CA3AF",
                        marginTop: 10,
                        textAlign: "center",
                      }}
                    >
                      Once saved, confirmation emails will be sent to the guest
                      and KTS team automatically.
                    </p>
                  </div>

                  {/* Reject option */}
                  {!this.state.showReject ? (
                    <div style={{ textAlign: "center", marginTop: 14 }}>
                      <button
                        onClick={() =>
                          this.setState({ showReject: true, error: null })
                        }
                        style={{
                          background: "none",
                          border: "none",
                          fontSize: 13,
                          color: "#DC2626",
                          cursor: "pointer",
                          textDecoration: "underline",
                          padding: 4,
                        }}
                      >
                        Unable to accommodate? Reject this booking
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        background: "#FEF2F2",
                        border: "1.5px solid #FECACA",
                        borderRadius: 12,
                        padding: "18px 20px",
                        marginTop: 14,
                      }}
                    >
                      <p
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#991B1B",
                          margin: "0 0 12px",
                        }}
                      >
                        ✗ Reject This Reservation
                      </p>
                      <textarea
                        rows={3}
                        value={this.state.rejectReason}
                        onChange={(e) =>
                          this.setState({
                            rejectReason: e.target.value,
                            error: null,
                          })
                        }
                        placeholder="Please provide a reason (e.g. No availability on requested dates)"
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          border: "1.5px solid #FECACA",
                          borderRadius: 10,
                          padding: "10px 12px",
                          fontSize: 13,
                          color: "#111827",
                          resize: "vertical",
                          outline: "none",
                          fontFamily: "Arial,sans-serif",
                          marginBottom: 10,
                        }}
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() =>
                            this.setState({
                              showReject: false,
                              rejectReason: "",
                              error: null,
                            })
                          }
                          style={{
                            flex: 1,
                            padding: "10px",
                            border: "1.5px solid #D1D5DB",
                            borderRadius: 10,
                            background: "#fff",
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#374151",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => this.handleReject()}
                          disabled={
                            this.state.rejecting ||
                            !this.state.rejectReason.trim()
                          }
                          style={{
                            flex: 2,
                            padding: "10px",
                            border: "none",
                            borderRadius: 10,
                            background: this.state.rejecting
                              ? "#9CA3AF"
                              : "#DC2626",
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#fff",
                            cursor: this.state.rejecting
                              ? "not-allowed"
                              : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                          }}
                        >
                          {this.state.rejecting ? (
                            <>
                              <RefreshCw
                                size={13}
                                style={{ animation: "spin 1s linear infinite" }}
                              />{" "}
                              Rejecting…
                            </>
                          ) : (
                            "✗ Confirm Rejection"
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <p
            style={{
              textAlign: "center",
              fontSize: 12,
              color: "#9CA3AF",
              marginTop: 20,
            }}
          >
            KTS Booking System &copy; {new Date().getFullYear()}
          </p>
        </div>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}

export default ConfirmPage;
